import React from 'react';
import { ClipLoader } from 'react-spinners';
import { 
    FaEdit, 
    FaTrashAlt, 
    FaCheckCircle, 
    FaFilePdf, 
    FaFileInvoiceDollar, 
    FaTools, 
    FaCopy,
    FaFileContract 
} from 'react-icons/fa';
import PresupuestoPDFLink from '../PresupuestoPDFLink'; 

const ListaPresupuestos = ({ 
    presupuestos, 
    cargando, 
    busqueda, 
    setBusqueda, 
    onEdit, 
    onDelete, 
    onApprove, 
    onCopy, 
    isApprovedView, 
    isOrderView 
}) => {

    if (cargando) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mt-2 text-center">
                <ClipLoader color="#14b8a6" />
                <p className="text-gray-500 mt-2 text-sm">Cargando datos de la GRID...</p>
            </div>
        );
    }

    const getStatusBadge = (status) => {
        const currentStatus = status || 'Borrador';
        switch (currentStatus) {
            case 'Aprobado':
                return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">Aprobado</span>;
            case 'Rechazado':
                return <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">Rechazado</span>;
            case 'Pendiente':
                return <span className="px-2 py-1 text-xs font-semibold text-blue-800 bg-blue-200 rounded-full">Pendiente</span>;
            default:
                return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full">Borrador</span>;
        }
    };

    const obtenerNombreCliente = (pres) => {
        if (!pres) return 'Sin Datos';
        if (pres.cliente) {
            if (typeof pres.cliente === 'object') {
                return pres.cliente.name || pres.cliente.nombre || pres.cliente.razonSocial || 'Sin Nombre';
            }
            if (typeof pres.cliente === 'string') return pres.cliente;
        }
        if (pres.datosCliente && pres.datosCliente.name) return pres.datosCliente.name;
        if (pres.nombreCliente) return pres.nombreCliente;
        return 'Sin Cliente';
    };

    // --- JARVIS: LÓGICA DE EXTRACCIÓN ROBUSTA PARA LA TABLA ---
    const formatearMontoSeguro = (pres) => {
        // Busca el total venta en todas las rutas posibles
        let totalVenta = parseFloat(
            pres.totales?.totalGeneral || 
            pres.calculos?.totales?.totalGeneral || 
            pres.calculos?.totalGeneral || 
            pres.total
        ) || 0;
        
        // Si el total es 0 pero hay líneas (presupuesto viejo/corrupto), lo calculamos al vuelo
        if (totalVenta === 0 && pres.lineas && pres.lineas.length > 0) {
            const sumaLineas = pres.lineas.reduce((acc, l) => acc + (parseFloat(l.total) || 0), 0);
            const comision = (sumaLineas * (parseFloat(pres.calculos?.comision) || parseFloat(pres.calculos?.comisionVendedor) || 10)) / 100;
            const flete = parseFloat(pres.calculos?.costoFlete) || parseFloat(pres.costoFlete) || 0;
            const inst = parseFloat(pres.calculos?.costoInstalacion) || parseFloat(pres.costoInstalacion) || 0;
            
            const subtotalVisible = sumaLineas + comision + flete + inst;
            const transporteEmergencia = parseFloat(pres.calculos?.transporteManualResumen) || parseFloat(pres.transporteManualResumen) || 0;
            const baseEmergencia = subtotalVisible + transporteEmergencia;
            totalVenta = baseEmergencia * 1.21; // Agregamos IVA
        }

        return totalVenta.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
    };

    const presupuestosValidos = Array.isArray(presupuestos) ? presupuestos : [];
    const title = isOrderView ? "Historial de Órdenes" : (isApprovedView ? "Presupuestos Aprobados" : "Historial de Presupuestos");

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mt-2">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
                <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                <input
                    type="text"
                    placeholder="Buscar por cliente o número..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="w-full sm:w-1/3 p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100 font-bold">
                        <tr>
                            <th scope="col" className="px-4 py-3">{isOrderView ? 'Nº Pedido' : 'Número'}</th>
                            <th scope="col" className="px-4 py-3">Fecha</th>
                            <th scope="col" className="px-4 py-3">Cliente</th>
                            <th scope="col" className="px-4 py-3">Total</th>
                            <th scope="col" className="px-4 py-3 text-center">Estado</th>
                            <th scope="col" className="px-4 py-3 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {presupuestosValidos.length > 0 ? (
                            presupuestosValidos.map((pres) => {
                                const esBorradorOPendiente = !pres.estado || pres.estado === 'Borrador' || pres.estado === 'Pendiente';

                                return (
                                    <tr key={pres.id} className="bg-white border-b hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-2 font-medium text-gray-900">
                                            {isOrderView ? (pres.pedidoVinculado || 'N/A') : (pres.numero || 'N/A')}
                                        </td>
                                        <td className="px-4 py-2">
                                            {pres.fecha ? new Date(pres.fecha).toLocaleDateString('es-ES') : 'N/A'}
                                        </td>
                                        <td className="px-4 py-2 font-medium text-gray-800">
                                            {obtenerNombreCliente(pres)}
                                        </td> 
                                        
                                        {/* AQUI ESTÁ LA MAGIA QUE ARREGLA EL 0 */}
                                        <td className="px-4 py-2 font-semibold text-teal-700">
                                            {formatearMontoSeguro(pres)}
                                        </td>

                                        <td className="px-4 py-2 text-center">
                                            {getStatusBadge(pres.estado)}
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <div className="flex justify-center items-center gap-2">
                                                
                                                {/* Botón de Aprobar */}
                                                {!isApprovedView && !isOrderView && onApprove && esBorradorOPendiente && (
                                                    <button 
                                                        onClick={() => onApprove(pres.id)} 
                                                        className="text-green-500 hover:text-green-700 text-lg p-1 transition-transform hover:scale-110" 
                                                        title="Aprobar Presupuesto"
                                                    >
                                                        <FaCheckCircle />
                                                    </button>
                                                )}
                                                
                                                {/* BOTÓN PDF CLIENTE / ORDEN */}
                                                <div title={isOrderView ? "Descargar Orden de Compra" : "Descargar Presupuesto Cliente"}>
                                                    <PresupuestoPDFLink 
                                                        presupuesto={pres} 
                                                        type={isOrderView ? "orden" : "presupuesto"} 
                                                        icon={
                                                            <span className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200 hover:bg-red-100 transition-colors text-xs font-bold shadow-sm cursor-pointer whitespace-nowrap">
                                                                <FaFilePdf /> {isOrderView ? "Orden" : "Cliente"}
                                                            </span>
                                                        } 
                                                    />
                                                </div>

                                                {!isOrderView && (
                                                    <>
                                                        {/* BOTÓN INTERNO */}
                                                        <div title="Descargar Cálculo Interno">
                                                            <PresupuestoPDFLink 
                                                                presupuesto={pres} 
                                                                type="interno" 
                                                                icon={
                                                                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100 transition-colors text-xs font-bold shadow-sm cursor-pointer whitespace-nowrap">
                                                                        <FaFileInvoiceDollar /> Interno
                                                                    </span>
                                                                } 
                                                            />
                                                        </div>

                                                        {/* BOTÓN TALLER */}
                                                        <div title="Descargar Hoja Taller">
                                                            <PresupuestoPDFLink 
                                                                presupuesto={pres} 
                                                                type="taller" 
                                                                icon={
                                                                    <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 transition-colors text-xs font-bold shadow-sm cursor-pointer whitespace-nowrap">
                                                                        <FaTools /> Taller
                                                                    </span>
                                                                } 
                                                            />
                                                        </div>

                                                        {/* BOTÓN ORDEN DE PEDIDO */}
                                                        <div title="Descargar Orden de Pedido">
                                                            <PresupuestoPDFLink 
                                                                presupuesto={pres} 
                                                                type="orden" 
                                                                icon={
                                                                    <span className="flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded border border-indigo-200 hover:bg-indigo-200 transition-colors text-xs font-bold shadow-sm cursor-pointer whitespace-nowrap">
                                                                        <FaFileContract /> Orden
                                                                    </span>
                                                                } 
                                                            />
                                                        </div>

                                                        {/* Divisor visual y botones de edición/copia */}
                                                        <div className="flex gap-2 ml-1 pl-2 border-l border-gray-200">
                                                            {onCopy && (
                                                                <button 
                                                                    onClick={() => onCopy(pres)} 
                                                                    className="text-orange-400 hover:text-orange-600 text-base transition-transform hover:scale-110" 
                                                                    title="Copiar Presupuesto (Crear Borrador)"
                                                                >
                                                                    <FaCopy />
                                                                </button>
                                                            )}

                                                            {onEdit && (
                                                                <button 
                                                                    onClick={() => onEdit(pres)} 
                                                                    className="text-purple-500 hover:text-purple-700 text-base transition-transform hover:scale-110" 
                                                                    title="Editar Presupuesto"
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                
                                                {/* BOTÓN ELIMINAR */}
                                                <button 
                                                    onClick={() => onDelete(pres.id)} 
                                                    className="text-gray-400 hover:text-red-500 text-base ml-1 transition-colors" 
                                                    title="Eliminar Documento"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-10 text-gray-500 italic bg-gray-50">
                                    No se encontraron documentos en esta sección de la GRID.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ListaPresupuestos;